/**
 * Secure Token Management Utility
 *
 * SECURITY MODEL:
 * - PRIMARY: httpOnly cookies (set by backend, inaccessible to JS, immune to XSS)
 * - BACKUP: In-memory token (for edge cases where cookies fail - proxy stripping, strict CORS)
 * - PERSISTENCE: sessionStorage (tab-scoped, cleared on close, obfuscated)
 *
 * The backend checks httpOnly cookies first, then falls back to Authorization header.
 * This utility manages the backup token for the Authorization header fallback.
 */

const SESSION_KEY = 'celltrack_session';

// In-memory token store - primary backup storage (XSS cannot directly access)
let memoryToken: string | null = null;

// Simple obfuscation (defense-in-depth, not encryption)
const obfuscate = (str: string | null): string | null => {
  if (!str) return null;
  return btoa(str.split('').reverse().join(''));
};

const deobfuscate = (str: string | null): string | null => {
  if (!str) return null;
  try {
    return atob(str).split('').reverse().join('');
  } catch (e: any) {
    console.warn('[Auth] Failed to deobfuscate token:', e.message);
    return null;
  }
};

/**
 * Get the current session token
 * Checks: memory -> sessionStorage -> localStorage (migration)
 */
export const getAuthToken = (): string | null => {
  if (memoryToken) {
    return memoryToken;
  }

  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      const token = deobfuscate(stored);
      if (token) {
        memoryToken = token;
        return token;
      }
    }
  } catch (e: any) {
    console.debug('[Auth] sessionStorage unavailable:', e.message);
  }

  // Migration: check localStorage for existing sessions
  try {
    const legacyToken = localStorage.getItem(SESSION_KEY);
    if (legacyToken) {
      memoryToken = legacyToken;
      try {
        sessionStorage.setItem(SESSION_KEY, obfuscate(legacyToken) as string);
        localStorage.removeItem(SESSION_KEY);
        console.debug('[Auth] Migrated token from localStorage to sessionStorage');
      } catch (e: any) {
        console.debug('[Auth] Storage blocked, using memory-only mode:', e.message);
      }
      return legacyToken;
    }
  } catch (e: any) {
    console.debug('[Auth] localStorage unavailable:', e.message);
  }

  return null;
};

/**
 * Set the session token
 * Stores in memory (primary) + sessionStorage (backup)
 */
export const setAuthToken = (token: string | null): void => {
  memoryToken = token;

  try {
    if (token) {
      sessionStorage.setItem(SESSION_KEY, obfuscate(token) as string);
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
    localStorage.removeItem(SESSION_KEY);
  } catch (e: any) {
    console.debug('[Auth] Storage blocked, using memory-only mode:', e.message);
  }
};

/** Clear the session token */
export const clearAuthToken = (): void => {
  setAuthToken(null);
};

/** Get authorization headers for API calls */
export const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/** Check if user has a session token */
export const hasAuthToken = (): boolean => {
  return !!getAuthToken();
};
